
import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface Artist {
  id: string;
  name: string;
  position: string;
  type: string;
  phone: string;
  email: string;
  specialties: string[];
  monthlyBookings: number;
}

interface Schedule {
  id: string;
  date: string;
  time: string;
  customerName: string;
  type: string;
  location: string;
}

export default function MakeupArtists() {
  const [selectedArtist, setSelectedArtist] = useState<string | null>('1');
  const [currentDate, setCurrentDate] = useState(new Date(2024, 2, 1));
  const [showAddForm, setShowAddForm] = useState(false);

  const artists: Artist[] = [
    {
      id: '1',
      name: '김원장',
      position: '원장',
      type: '정규직',
      phone: '010-1234-5678',
      email: 'kim@makeup.com',
      specialties: ['글램', '클래식', '내추럴'],
      monthlyBookings: 28
    },
    {
      id: '2',
      name: '박실장',
      position: '실장',
      type: '정규직',
      phone: '010-2345-6789',
      email: 'park@makeup.com',
      specialties: ['세미글램', '내추럴'],
      monthlyBookings: 25
    },
    {
      id: '3',
      name: '최팀장',
      position: '팀장',
      type: '정규직',
      phone: '010-3456-7890',
      email: 'choi@makeup.com',
      specialties: ['혼주메이크업', '클래식'],
      monthlyBookings: 22
    },
    {
      id: '4',
      name: '이아티스트',
      position: '아티스트',
      type: '정규직',
      phone: '010-4567-8901',
      email: 'lee@makeup.com',
      specialties: ['내추럴', '세미글램'],
      monthlyBookings: 20
    },
    {
      id: '5',
      name: '프리랜서A',
      position: '프리랜서',
      type: '외주',
      phone: '010-5678-9012',
      email: 'freelancer@makeup.com',
      specialties: ['글램', '출장메이크업'],
      monthlyBookings: 15
    }
  ];

  const schedules: Record<string, Schedule[]> = {
    '1': [
      { id: '1', date: '2024-03-15', time: '11:00', customerName: '이지은', type: '리허설메이크업', location: '매장' },
      { id: '2', date: '2024-03-18', time: '09:00', customerName: '한소영', type: '본식메이크업', location: '출장' },
      { id: '3', date: '2024-03-20', time: '13:00', customerName: '정수진', type: '리허설메이크업', location: '매장' }
    ],
    '2': [
      { id: '4', date: '2024-03-15', time: '09:00', customerName: '김미영', type: '본식메이크업', location: '매장' },
      { id: '5', date: '2024-03-16', time: '10:00', customerName: '정민호', type: '본식메이크업', location: '출장' },
      { id: '6', date: '2024-03-22', time: '14:00', customerName: '박지은', type: '리허설메이크업', location: '매장' }
    ],
    '3': [
      { id: '7', date: '2024-03-15', time: '14:00', customerName: '박수진', type: '혼주메이크업', location: '출장' },
      { id: '8', date: '2024-03-19', time: '11:00', customerName: '최영희', type: '혼주메이크업', location: '매장' }
    ],
    '4': [
      { id: '9', date: '2024-03-17', time: '10:00', customerName: '윤서연', type: '본식메이크업', location: '매장' },
      { id: '10', date: '2024-03-21', time: '15:00', customerName: '강민지', type: '리허설메이크업', location: '매장' }
    ],
    '5': [
      { id: '11', date: '2024-03-16', time: '13:00', customerName: '송하나', type: '출장메이크업', location: '출장' },
      { id: '12', date: '2024-03-23', time: '10:00', customerName: '임유진', type: '본식메이크업', location: '출장' }
    ]
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getSchedulesForDate = (dateString: string) => {
    if (!selectedArtist) return [];
    return schedules[selectedArtist]?.filter(schedule => schedule.date === dateString) || [];
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    const weekHeader = weekDays.map(day => (
      <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50">
        {day}
      </div>
    ));

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-24 border border-gray-200"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDate(currentDate.getFullYear(), currentDate.getMonth(), day);
      const daySchedules = getSchedulesForDate(dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];

      days.push(
        <div
          key={day}
          className={`p-2 h-24 border border-gray-200 ${
            isToday ? 'bg-pink-50' : ''
          }`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-pink-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {daySchedules.slice(0, 2).map((schedule) => (
              <div
                key={schedule.id}
                className="text-xs p-1 rounded truncate bg-pink-100 text-pink-800"
              >
                {schedule.time} {schedule.customerName}
              </div>
            ))}
            {daySchedules.length > 2 && (
              <div className="text-xs text-gray-500">
                +{daySchedules.length - 2}개 더
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7">
          {weekHeader}
          {days}
        </div>
      </div>
    );
  };

  const selectedArtistData = artists.find(a => a.id === selectedArtist);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">아티스트 배정 관리</h1>
            <p className="text-sm text-gray-600 mt-1">아티스트별 스케줄을 관리하세요</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            아티스트 등록
          </button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* 아티스트 목록 */}
          <div className="col-span-4">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">아티스트 목록</h3>
              <div className="space-y-2">
                {artists.map((artist) => (
                  <div
                    key={artist.id}
                    onClick={() => setSelectedArtist(artist.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedArtist === artist.id
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-gray-200 hover:border-pink-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                          <i className="ri-user-line text-pink-600"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{artist.name}</h4>
                          <p className="text-sm text-gray-600">{artist.position}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        artist.type === '정규직' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {artist.type}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {artist.specialties.map((specialty, index) => (
                        <span key={index} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                          {specialty}
                        </span>
                      ))}
                    </div>
                    <div className="text-sm text-gray-600">
                      이번 달 예약: <span className="font-semibold text-pink-600">{artist.monthlyBookings}건</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 아티스트 스케줄 */}
          <div className="col-span-8">
            {selectedArtistData ? (
              <>
                {/* 아티스트 정보 */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-2xl text-pink-600"></i>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{selectedArtistData.name}</h2>
                        <p className="text-gray-600">{selectedArtistData.position} • {selectedArtistData.type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">이번 달 예약</div>
                      <div className="text-3xl font-bold text-pink-600">{selectedArtistData.monthlyBookings}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">연락처</div>
                      <div className="font-medium text-gray-900">{selectedArtistData.phone}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">이메일</div>
                      <div className="font-medium text-gray-900">{selectedArtistData.email}</div>
                    </div>
                  </div>
                </div>

                {/* 캘린더 헤더 */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={goToPreviousMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-gray-600"></i>
                    </button>
                    <h2 className="text-xl font-semibold text-gray-800">
                      {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                    </h2>
                    <button
                      onClick={goToNextMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-gray-600"></i>
                    </button>
                  </div>
                </div>

                {/* 캘린더 */}
                {renderCalendar()}

                {/* 예약 목록 */}
                <div className="mt-6 bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">이번 달 예약 목록</h3>
                  <div className="space-y-3">
                    {schedules[selectedArtist]?.map((schedule) => (
                      <div key={schedule.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                            <i className="ri-calendar-line text-pink-600"></i>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{schedule.customerName}</div>
                            <div className="text-sm text-gray-600">
                              {schedule.date} {schedule.time} • {schedule.type}
                            </div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 text-sm rounded-full ${
                          schedule.location === '매장' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {schedule.location}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <i className="ri-user-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">아티스트를 선택하세요</p>
              </div>
            )}
          </div>
        </div>

        {/* 아티스트 등록 모달 */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">아티스트 등록</h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              
              <form className="space-y-4" onSubmit={(e) => {
                e.preventDefault();
                // Handle form submission here
                console.log('Form submitted');
                setShowAddForm(false);
              }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                  <input 
                    type="text" 
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">직급</label>
                    <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer">
                      <option>원장</option>
                      <option>실장</option>
                      <option>팀장</option>
                      <option>아티스트</option>
                      <option>프리랜서</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고용형태</label>
                    <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer">
                      <option>정규직</option>
                      <option>외주</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                  <input 
                    type="tel" 
                    placeholder="010-0000-0000"
                    pattern="[0-9]{3}-[0-9]{4}-[0-9]{4}"
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                  <input 
                    type="email" 
                    placeholder="email@example.com"
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 whitespace-nowrap cursor-pointer"
                  >
                    등록
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
